// src/pages/orders/components/bulk/DownloadTemplateModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import styles from './DownloadTemplateModal.module.css';

const STORAGE_KEY = 'bulk-order-template-options';

const DEFAULT_FIELDS = {
  // Required fields (always included, not shown in modal)
  customerName: true,
  address: true,
  phone1: true,
  phone2: true,
  cityName: true,

  // Optional fields (can be toggled)
  customerEmail: true,
  paymentType: true,
  orderStatus: true,
  privateReference: true,
  deliveryNotes: true,
  specialNotes: true,
  deliveryCharge: true,
  totalDiscount: true,
  customLineTotal: true
};

const DEFAULT_NUMBER_OF_ITEMS = 3;

const DownloadTemplateModal = ({ onClose, onDownload, isGenerating }) => {
  const [selectedFields, setSelectedFields] = useState(DEFAULT_FIELDS);
  const [numberOfItems, setNumberOfItems] = useState(DEFAULT_NUMBER_OF_ITEMS);
  const [isLoadingPrefs, setIsLoadingPrefs] = useState(true);

  // Load saved preferences from storage on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const result = await window.storage.get(STORAGE_KEY);
        if (result && result.value) {
          const saved = JSON.parse(result.value);
          if (saved.selectedFields) {
            // Merge with defaults to handle any new fields added after last save
            setSelectedFields(prev => ({ ...prev, ...saved.selectedFields }));
          }
          if (typeof saved.numberOfItems === 'number') {
            setNumberOfItems(saved.numberOfItems);
          }
        }
      } catch {
        // Key doesn't exist yet or parse error — use defaults silently
      } finally {
        setIsLoadingPrefs(false);
      }
    };

    loadPreferences();
  }, []);

  // Persist preferences to storage
  const savePreferences = async (fields, items) => {
    try {
      await window.storage.set(STORAGE_KEY, JSON.stringify({
        selectedFields: fields,
        numberOfItems: items
      }));
    } catch (err) {
      console.error('Failed to save template preferences:', err);
    }
  };

  const optionalFields = [
    { key: 'customerEmail', label: 'Customer_Email', category: 'Customer' },
    { key: 'deliveryNotes', label: 'Delivery_Notes', category: 'Customer' },
    { key: 'specialNotes', label: 'Special_Notes', category: 'Customer' },
    { key: 'privateReference', label: 'Private_Reference', category: 'Order' },
    { key: 'paymentType', label: 'Payment_Type', category: 'Order' },
    { key: 'orderStatus', label: 'Order_Status', category: 'Order' },
    { key: 'customLineTotal', label: 'Custom_Line_Total (per item)', category: 'Items' },
    { key: 'deliveryCharge', label: 'Delivery_Charge', category: 'Items' },
    { key: 'totalDiscount', label: 'Total_Discount', category: 'Items' },
  ];

  const categories = ['Customer', 'Delivery', 'Order', 'Items', 'Financial'];

  const handleToggleField = (fieldKey) => {
    setSelectedFields(prev => {
      const updated = { ...prev, [fieldKey]: !prev[fieldKey] };
      savePreferences(updated, numberOfItems);
      return updated;
    });
  };

  const handleNumberOfItemsChange = (num) => {
    setNumberOfItems(num);
    savePreferences(selectedFields, num);
  };

  const handleSelectAll = () => {
    const allSelected = {};
    Object.keys(selectedFields).forEach(key => {
      allSelected[key] = true;
    });
    setSelectedFields(allSelected);
    savePreferences(allSelected, numberOfItems);
  };

  const handleDeselectAll = () => {
    const noneSelected = {};
    Object.keys(selectedFields).forEach(key => {
      noneSelected[key] = key === 'customerName' || key === 'address' ||
                          key === 'phone1' || key === 'cityName';;
    });
    setSelectedFields(noneSelected);
    savePreferences(noneSelected, numberOfItems);
  };

  const handleDownload = () => {
    onDownload({
      selectedFields,
      numberOfItems
    });
  };

  const getFieldsByCategory = (category) => {
    return optionalFields.filter(field => field.category === category);
  };

  if (isLoadingPrefs) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal}>
          <div className={styles.loadingPrefs}>
            <div className={styles.spinner}></div>
            <span>Loading preferences...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h3>Customize Template</h3>
            <p>Select the columns you want to include in your template</p>
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Required Fields Info */}
          <div className={styles.requiredInfo}>
            <strong>Required fields (always included):</strong>
            <div className={styles.requiredFields}>
              <span>Customer_Name</span>
              <span>Address</span>
              <span>Phone_1</span>
              <span>Phone_2</span>
              <span>City_Name</span>
              <span>Item_1_SKU</span>
              <span>Item_1_Quantity</span>
            </div>
          </div>

          {/* Number of Items Selector */}
          <div className={styles.itemsSelector}>
            <label>Number of Item Columns (1-5):</label>
            <div className={styles.itemsButtons}>
              {[1, 2, 3, 4, 5].map(num => (
                <button
                  key={num}
                  className={numberOfItems === num ? styles.active : ''}
                  onClick={() => handleNumberOfItemsChange(num)}
                >
                  {num}
                </button>
              ))}
            </div>
            <p className={styles.helperText}>
              Each item includes: SKU, Quantity{selectedFields.customLineTotal ? ', and Custom Line Total' : ''}
            </p>
          </div>

          {/* Quick Actions */}
          <div className={styles.quickActions}>
            <button onClick={handleSelectAll} className={styles.quickButton}>
              Select All Optional
            </button>
            <button onClick={handleDeselectAll} className={styles.quickButton}>
              Deselect All Optional
            </button>
          </div>

          {/* Optional Fields by Category */}
          <div className={styles.fieldsContainer}>
            {categories.map(category => {
              const categoryFields = getFieldsByCategory(category);
              if (categoryFields.length === 0) return null;

              return (
                <div key={category} className={styles.category}>
                  <h4>{category}</h4>
                  <div className={styles.fields}>
                    {categoryFields.map(field => (
                      <label key={field.key} className={styles.field}>
                        <input
                          type="checkbox"
                          checked={selectedFields[field.key] ?? true}
                          onChange={() => handleToggleField(field.key)}
                        />
                        <span>{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.cancelButton}>
            Cancel
          </button>
          <button
            onClick={handleDownload}
            className={styles.downloadButton}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <div className={styles.spinner}></div>
                Generating...
              </>
            ) : (
              <>
                <Download size={18} />
                Download Template
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadTemplateModal;